import { CloudCustomerAddress } from "@omni/types/api/customer/cloud/address.model";
import { Alert } from "@omni/ui/components/Alert/alert.component";
import { Button } from "@omni/ui/components/Button/button.component";
import { Divider } from "@omni/ui/components/Divider/divider.component";
import { cn } from "@omni/utils/styles";

import DefaultAddresses from "@/components/Account/Profile/ProfileAddressBook/DefaultAddresses/defaultAddresses.component";
import OtherAddresses from "@/components/Account/Profile/ProfileAddressBook/OtherAddresses/otherAddresses.component";
import { useAddressValidationContext } from "@/components/Address/AddressValidation/context/addressValidation.context";
import { Col } from "@/components/General/Grid/col.component";
import { Container } from "@/components/General/Grid/container.component";
import { Row } from "@/components/General/Grid/row.component";
import { Spinner } from "@/components/General/Spinner/spinner.component";
import { TError } from "@/redux/modules/value/errors/errors.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";

type AddressBookProps = {
    error: Array<TError> | undefined;
    loading: boolean;
    addresses: Array<CloudCustomerAddress>;
    updateDefaultBilling: (address: CloudCustomerAddress) => Promise<void>;
    updateDefaultDelivery: (address: CloudCustomerAddress) => Promise<void>;
};

const AddressBook = ({
    loading,
    error,
    updateDefaultDelivery,
    updateDefaultBilling,
    addresses = []
}: AddressBookProps) => {
    const { t } = useGranite();

    const { openAddressModal } = useAddressValidationContext();

    const defaultPaymentAddress = addresses?.find(
        (address) => address.defaultPaymentAddress
    );
    const defaultDeliveryAddress = addresses?.find(
        (address) => address.defaultDeliveryAddress
    );
    const otherAddresses = addresses.filter(
        (address) =>
            !address.defaultDeliveryAddress && !address.defaultPaymentAddress
    );
    const canDelete = addresses.length > 1;

    return (
        <>
            {error &&
                !loading &&
                error.map((err, index) => (
                    <Alert
                        key={index}
                        color="danger"
                        closeAriaLabel={t("general.alert.close")}
                    >
                        {err.message}
                    </Alert>
                ))}
            {loading &&
                (!otherAddresses ||
                    !defaultDeliveryAddress ||
                    !defaultPaymentAddress) && <Spinner />}
            {!loading &&
                (!otherAddresses ||
                    (!defaultDeliveryAddress && !defaultPaymentAddress)) && (
                    <div>{t("general.profile.addressbook.noAddresses")}</div>
                )}
            {(!loading ||
                otherAddresses ||
                defaultDeliveryAddress ||
                defaultPaymentAddress) && (
                <Container>
                    <DefaultAddresses
                        defaultDeliveryAddress={defaultDeliveryAddress}
                        defaultPaymentAddress={defaultPaymentAddress}
                        updateDefaultDelivery={updateDefaultDelivery}
                        updateDefaultBilling={updateDefaultBilling}
                        canDelete={canDelete}
                    />
                    <OtherAddresses
                        addresses={otherAddresses}
                        updateDefaultBilling={updateDefaultBilling}
                        updateDefaultDelivery={updateDefaultDelivery}
                        canDelete={canDelete}
                    />
                    <Row>
                        <Col>
                            <Divider className={cn("mb-3")} />
                            <Button
                                data-test="profileAddressBook-button-add"
                                color="link"
                                type="button"
                                onClick={() => openAddressModal()}
                            >
                                <i className="las la-plus mr-2" />
                                {t("address.action.add")}
                            </Button>
                        </Col>
                    </Row>
                </Container>
            )}
        </>
    );
};

export default AddressBook;
